import { MutualFundListItem } from '../power-rebalancing/MutualFundList'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import MultipleRecommendationsBottomSheet from './MultipleRecommendationsBottomSheet'
import {
  RegularFund,
  RegularToDirectSummaryResponse
} from '../../types/regular-to-direct-plan.types'
import MutualFundListItemSkeleton from '../skeletons/MutualFundListItemSkeleton'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'

const toPerformanceForm = (rating: unknown): PowerPerformanceForms => {
  if (Object.values(PowerPerformanceForms).includes(rating as PowerPerformanceForms)) {
    return rating as PowerPerformanceForms
  }
  return PowerPerformanceForms.NOT_RATED
}

/**
 * Pre-change rebalance entry gate, extended for allocation:
 * - Plan live → multi-rec only if fund is recommended for that live plan
 * - Neither live → multi-rec for Out of Form / Off Track (drop-time nudge path)
 */
function shouldOpenMultipleRecommendations(
  fund: RegularFund,
  allocationPlanAvailable: boolean,
  rebalancePlanAvailable: boolean,
  performanceForm: PowerPerformanceForms
): boolean {
  if (!fund.directSwitchRecommended) return false

  if (allocationPlanAvailable || rebalancePlanAvailable) {
    const recommendedForLiveAllocation =
      allocationPlanAvailable && fund.allocationRecommended === true
    const recommendedForLiveRebalance =
      rebalancePlanAvailable && fund.rebalanceRecommended === true
    return recommendedForLiveAllocation || recommendedForLiveRebalance
  }

  return (
    performanceForm === PowerPerformanceForms.OUT_OF_FORM ||
    performanceForm === PowerPerformanceForms.OFF_TRACK
  )
}

interface ListOfCommisionFundsProps {
  summaryData: RegularToDirectSummaryResponse | undefined
  isLoading: boolean
  isError: boolean
}

export default function ListOfCommisionFunds({
  summaryData,
  isLoading,
  isError
}: ListOfCommisionFundsProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const navigate = useNavigate()
  const rebalancePlanAvailable = summaryData?.rebalancePlanAvailable ?? false
  const allocationPlanAvailable = summaryData?.allocationPlanAvailable ?? false
  const commissionFunds =
    summaryData?.regularFunds?.filter((fund) => !fund.exited && fund.directSwitchRecommended) || []

  if (isLoading) {
    return (
      <div>
        {[...Array(3)].map((_, index) => (
          <MutualFundListItemSkeleton key={index} isLast={index === 2} showChevron={true} />
        ))}
      </div>
    )
  }

  if (isError || !summaryData || commissionFunds.length === 0) {
    return null
  }

  return (
    <>
      {commissionFunds.map((fund, index) => {
        const isLast = index === commissionFunds.length - 1
        const performanceForm = toPerformanceForm(fund.performanceRating)

        return (
          <MutualFundListItem
            key={fund.isin}
            isin={fund.isin}
            name={fund.name}
            fundNature={fund.fundNature}
            fundCategory={fund.fundCategory}
            purchaseMode={fund.purchaseMode}
            powerRank={fund.powerRank}
            performanceRating={performanceForm}
            missedGains={0}
            highExposure={false}
            mfLogoUrl={fund.mfLogoUrl}
            formType={performanceForm}
            sipAmount={0}
            isRegular={fund.purchaseMode === 'REGULAR'}
            riskProfile={null}
            borderRequired={true}
            isLast={isLast}
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'CommissionFundClick',
                  responseName: 'Click',
                  screenType: 'REGULAR_TO_DIRECT_PLAN_ELITE_PAGE'
                }
              })

              if (
                shouldOpenMultipleRecommendations(
                  fund,
                  allocationPlanAvailable,
                  rebalancePlanAvailable,
                  performanceForm
                )
              ) {
                trackEvent({
                  eventName: EventNames.UserClicked,
                  payload: {
                    eventId: 'OpenMultipleRecommendations',
                    responseName: 'Open',
                    screenType: 'REGULAR_TO_DIRECT_PLAN_ELITE_PAGE'
                  }
                })
                openBottomSheet(
                  <MultipleRecommendationsBottomSheet
                    isin={fund.isin}
                    onDismiss={() => {
                      trackEvent({
                        eventName: EventNames.UserClicked,
                        payload: {
                          eventId: 'MultipleRecommendationsBottomSheet',
                          responseName: 'Close',
                          screenType: 'REGULAR_TO_DIRECT_PLAN_ELITE_PAGE'
                        }
                      })
                      closeBottomSheet()
                    }}
                    allocationPlanAvailable={allocationPlanAvailable}
                    rebalancePlanAvailable={rebalancePlanAvailable}
                    allocationRecommended={fund.allocationRecommended === true}
                    rebalanceRecommended={fund.rebalanceRecommended === true}
                    timeToAllocationPlanDrop={summaryData.timeToAllocationPlanDrop}
                    timeToRebalancePlanDrop={summaryData.timeToRebalancePlanDrop}
                    isNewRebalance={summaryData.isNewRebalance === true}
                  />
                )
                return
              }

              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'NavigateToL2Page',
                  responseName: 'Navigate',
                  screenType: 'REGULAR_TO_DIRECT_PLAN_ELITE_PAGE'
                }
              })
              navigate(`/regular-to-direct-plan-elite/${fund.isin}`)
            }}
            additionalCommissionsPaid={fund.additionalCommissionsPaid}
            regularToDirectComparison={fund.regularToDirectComparison}
            directFundName={fund.directFundName}
            directFundISIN={fund.directFundIsin}
          />
        )
      })}
    </>
  )
}
